import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, delay, retry } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { ErrorService } from './error.service';
import { NetworkService } from './network.service';
import { Inject, Injectable, Injector } from '@angular/core';
import { APP_CONFIG, AppConfig } from '../../config/app-config';

// TODO : rename to legacy/qdrive-rest-service?
@Injectable({
  providedIn: 'root'
})
export abstract class RestService {
  private API_URL;

  constructor(
    @Inject(APP_CONFIG) appConfig: AppConfig,
    protected readonly http: HttpClient,
    protected readonly errorService: ErrorService,
    protected readonly networkService: NetworkService,
    protected readonly injector: Injector
  ) {
    this.API_URL = appConfig.baseApiUrl;
  }

  readonly baseHttpHeaders: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json'
  });

  readonly API_VERSION = 'v1';
  readonly DRIVER_DATA_URL = '/driver-data';
  readonly TOUR_URL = '/tour';
  readonly ACTION_UPDATES_URL = '/actions-status-update';
  readonly CHECKLIST_URL = '/checklist';
  readonly SHIFT_URL = '/shift';
  readonly SHIFT_BREAK_URL = '/shift-break';
  readonly TOUR_STOP_TRACKING_TRACKING_URL = '/tour-stop-tracking'; // Deprecated
  readonly TOUR_STOP_TRACKING_CHECK_IN_URL = '/check-in';
  readonly TOUR_STOP_TRACKING_CHECK_OUT_URL = '/check-out';
  readonly REPORT_SHIFT_URL = '/activity-report';
  readonly SEARCH_MACHINE_URL = '/search-machine';
  readonly SWITCH_MACHINE_URL = '/switch-machine';
  readonly FUEL_REFILL_URL = '/fuel-refill';
  readonly UPSERT_DRIVER_NOTE = '/upsert-driver-note';
  readonly DELETE_DRIVER_NOTE = '/delete-driver-note';
  // todo change driver urls for consistency
  readonly GET_DRIVER_NAMES_URL = '/get-driver-names';
  readonly ADD_DRIVER_NAME_URL = '/add-driver-name';
  readonly SET_LANG_URL = '/set-lang';
  readonly TRANSPORT_FILE_URL = '/transport-file';

  protected get(endpoint: string, queryParams?: HttpParams, ignoreErrorToast?: boolean): Observable<any> {
    return this.http
      .get(`${this.API_URL}${this.API_VERSION}${endpoint}`, { headers: this.baseHttpHeaders, params: queryParams })
      .pipe(
        catchError((error) => this.errorService.handleError(error, ignoreErrorToast)),
        retry(1)
      );
  }

  protected post(
    endpoint: string,
    payload: any,
    queryParams?: HttpParams,
    ignoreErrorToast?: boolean
  ): Observable<any> {
    const isOnline = this.networkService.checkOnlineStatus();
    if (!isOnline) {
      this.networkService.queueRequest({ type: 'post', endpoint, payload });
      return of(this.offlineResponse()).pipe(delay(50));
    }
    return this.http
      .post(`${this.API_URL}${this.API_VERSION}${endpoint}`, payload, {
        headers: this.baseHttpHeaders,
        params: queryParams
      })
      .pipe(
        catchError((error) => this.errorService.handleError(error, ignoreErrorToast)),
        retry(1)
      );
  }

  protected put(endpoint: string, payload: any, queryParams?: HttpParams): Observable<any> {
    const isOnline = this.networkService.checkOnlineStatus();
    if (!isOnline) {
      this.networkService.queueRequest({ type: 'put', endpoint, payload });
      return of(this.offlineResponse()).pipe(delay(50));
    }
    return this.http
      .put(`${this.API_URL}${this.API_VERSION}${endpoint}`, payload, {
        headers: this.baseHttpHeaders,
        params: queryParams
      })
      .pipe(
        catchError((error) => this.errorService.handleError(error)),
        retry(1)
      );
  }

  protected delete(endpoint: string, queryParams?: HttpParams): Observable<any> {
    const isOnline = this.networkService.checkOnlineStatus();
    if (!isOnline) {
      this.networkService.queueRequest({ type: 'delete', endpoint, payload: null });

      return of(this.offlineResponse()).pipe(delay(50));
    }
    return this.http
      .put(`${this.API_URL}${this.API_VERSION}${endpoint}`, { headers: this.baseHttpHeaders, params: queryParams })
      .pipe(
        catchError((error) => this.errorService.handleError(error)),
        retry(1)
      );
  }

  /** Tag for illegal offline-generated IDs, making them recognizable. */
  readonly OFFLINE_ID_TAG: string = 'illegal-offline-id';

  /**
   * Generates the default offline response, assuming successful back-end processing.
   *
   * WARNING: This response will contain a client-side generated ID that will *not* be known in the back-end.
   *          This ID will not cause issues until it is attempted to be used in back-end communication.
   *          Since we do not know if these IDs will in fact be used in back-end communication (not all calls require
   * IDs to be passed), we will not yet solve this issue. The "illegal" IDs are clearly tagged to be recognizable, in
   * the front-end state and in the back-end calls. When we do notice issues with them being used, we will have to
   * tackle these issues
   *          (e.g., by demanding a client-side state refresh - to obtain the correct back-end IDs, when they are not
   * yet available - before the impacted functionality can be used, or by using client-side IDs for the impacted
   * functionality in front- and back-end.)
   */
  public offlineResponse() {
    const entityID = `${this.OFFLINE_ID_TAG}-${uuidv4()}`; // unique, tagged ID

    // Support array and non-array response data
    const responseDataWithID: any = [{ id: entityID }];
    responseDataWithID.id = entityID;

    const response = {
      status: true, // success
      data: responseDataWithID
    };

    return response;
  }
}
